import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, OnInit, HostListener, inject } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatStepperModule } from '@angular/material/stepper';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { ProjectDeleteService, ProjectDeleteResult as ServiceResult } from '../../../../shared/project-delete.service';

export interface ProjectDeleteDialogData {
  projectId: string;
  projectName?: string;
  fileCount?: number;
  totalSizeBytes?: number;
  estimatedCost?: number; // Future enhancement
  showHardDeleteOption?: boolean; // Show only in dev/test environments
}

export interface ProjectDeleteResult {
  success: boolean;
  projectId?: string;
  filesDeleted?: number;
  bytesDeleted?: number;
  deletedFilePaths?: string[];
  forceHardDelete?: boolean;
  error?: string;
}

/** Confirmation dialog for project deletion, requiring the user to type the project name and showing file/cost impact. */
@Component({
  selector: 'app-project-delete-dialog',
  templateUrl: './project-delete-dialog.component.html',
  styleUrls: ['./project-delete-dialog.component.scss'],
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressBarModule,
    MatCardModule,
    MatCheckboxModule,
    MatRadioModule,
    MatStepperModule,
    MatExpansionModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class ProjectDeleteDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ProjectDeleteDialogComponent>>(MatDialogRef);
  data = inject<ProjectDeleteDialogData>(MAT_DIALOG_DATA);
  private projectDeleteService = inject(ProjectDeleteService);

  confirmationDeleteText: string = '';
  confirmationProjectId: string = '';
  deletionMode: string = 'soft'; // Default to soft delete
  currentStep: number = 0; // 0 = warning step, 1 = confirmation step
  deletionOptionsExpanded: boolean = false;
  loading = false;
  error: string | null = null;

  // Reactive form controls for validation
  deleteTextFormControl = new FormControl('', {
    validators: [Validators.required, this.deleteTextValidator.bind(this)],
  });

  projectIdFormControl = new FormControl('', {
    validators: [Validators.required, this.projectIdValidator.bind(this)],
  });

  ngOnInit(): void {
    // Initialize form controls
    this.deleteTextFormControl.setValue('');
    this.projectIdFormControl.setValue('');
  }

  /**
   * Handle escape key press to close dialog
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.onCancel();
  }

  /**
   * Custom validator to ensure the confirmation text is exactly "DELETE"
   */
  deleteTextValidator(control: FormControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) {
      return null; // Let required validator handle empty values
    }

    if (value !== 'DELETE') {
      return { invalidDeleteText: { value: value, expected: 'DELETE' } };
    }

    return null;
  }

  /**
   * Custom validator to ensure the confirmation project ID matches exactly
   */
  projectIdValidator(control: FormControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) {
      return null; // Let required validator handle empty values
    }

    if (value !== this.data.projectId) {
      return { invalidProjectId: { value: value, expected: this.data.projectId } };
    }

    return null;
  }

  /**
   * Check if the delete button should be enabled
   */
  canDelete(): boolean {
    return (
      this.deleteTextFormControl.valid &&
      this.projectIdFormControl.valid &&
      !this.loading &&
      this.confirmationDeleteText === 'DELETE' &&
      this.confirmationProjectId === this.data.projectId
    );
  }

  /**
   * Format bytes for display
   */
  formatBytes(bytes?: number): string {
    if (!bytes || bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get error message for delete text validation
   */
  getDeleteTextErrorMessage(): string {
    if (this.deleteTextFormControl.hasError('required')) {
      return 'You must type DELETE to confirm';
    }
    if (this.deleteTextFormControl.hasError('invalidDeleteText')) {
      return 'Must be exactly "DELETE" (case-sensitive)';
    }
    return '';
  }

  /**
   * Get error message for project ID validation
   */
  getProjectIdErrorMessage(): string {
    if (this.projectIdFormControl.hasError('required')) {
      return 'You must type the project ID to confirm';
    }
    if (this.projectIdFormControl.hasError('invalidProjectId')) {
      return `Must be exactly "${this.data.projectId}"`;
    }
    return '';
  }

  /**
   * Handle form submission
   */
  onDelete(): void {
    if (!this.canDelete()) {
      return;
    }

    this.loading = true;
    this.error = null;

    // Make the actual gRPC call
    const deleteOptions = {
      projectId: this.data.projectId,
      confirmationDeleteText: this.confirmationDeleteText,
      confirmationProjectId: this.confirmationProjectId,
      forceHardDelete: this.deletionMode === 'hard',
    };

    ClientLoggingService.log('INFO', '🗑️ Dialog: Making delete service call with options:', deleteOptions);

    this.projectDeleteService.deleteProject(deleteOptions).subscribe({
      next: (result: ServiceResult) => {
        ClientLoggingService.log('INFO', '🗑️ Dialog: Delete service call successful:', result);
        this.loading = false;

        const dialogResult: ProjectDeleteResult = {
          success: true,
          projectId: result.projectId,
          filesDeleted: result.filesDeleted,
          bytesDeleted: result.bytesDeleted,
          deletedFilePaths: result.deletedFilePaths,
          forceHardDelete: this.deletionMode === 'hard',
        };

        this.dialogRef.close(dialogResult);
      },
      error: (error: any) => {
        ClientLoggingService.log('ERROR', '🗑️ Dialog: Delete service call failed:', error);
        this.loading = false;
        this.error = error.error || error.message || 'Failed to delete project';

        // Don't close dialog on error - let user see the error and try again or cancel
      },
    });
  }

  /**
   * Handle cancel button
   */
  onCancel(): void {
    this.dialogRef.close(null);
  }

  /**
   * Move to next step (warning -> confirmation)
   */
  onContinue(): void {
    this.currentStep = 1;
  }

  /**
   * Move back to previous step (confirmation -> warning)
   */
  onBack(): void {
    this.currentStep = 0;
    this.error = null; // Clear any errors when going back
  }

  /**
   * Check if we're on the warning step
   */
  isWarningStep(): boolean {
    return this.currentStep === 0;
  }

  /**
   * Check if we're on the confirmation step
   */
  isConfirmationStep(): boolean {
    return this.currentStep === 1;
  }

  /**
   * Update confirmation text and trigger validation
   */
  onDeleteTextChange(): void {
    this.deleteTextFormControl.setValue(this.confirmationDeleteText);
  }

  /**
   * Update confirmation project ID and trigger validation
   */
  onProjectIdChange(): void {
    this.projectIdFormControl.setValue(this.confirmationProjectId);
  }

  /**
   * Check if hard delete option should be shown (dev/test environments only)
   */
  shouldShowHardDeleteOption(): boolean {
    return this.data.showHardDeleteOption === true && (this.isTestEnvironment() || this.isDevelopmentEnvironment());
  }

  /**
   * Check if current environment is test
   */
  private isTestEnvironment(): boolean {
    return (
      window.location.hostname.includes('test') ||
      window.location.hostname.includes('localhost') ||
      window.location.port === '4200'
    );
  }

  /**
   * Check if current environment is development
   */
  private isDevelopmentEnvironment(): boolean {
    return (
      window.location.hostname.includes('dev') ||
      window.location.hostname.includes('localhost') ||
      window.location.port === '4200'
    );
  }

  /**
   * Get the delete button text - always the same regardless of deletion mode
   */
  getDeleteButtonText(): string {
    return 'DELETE PROJECT';
  }

  /**
   * Check if hard delete is selected
   */
  isHardDeleteSelected(): boolean {
    return this.deletionMode === 'hard';
  }
}
